// صف تیکت‌های پشتیبانی Phoenix در Console (US-CHN-045؛ platform.support_ticket.handle — C-AGENT-425): تیکت‌های مستأجرها با
// وضعیت و «منتظر پشتیبانی»؛ جزئیات با داده فنی (نسخه، مسیر بدون شناسه، شناسه همبستگی)، تصویر صفحه، گفت‌وگو و پاسخ با تغییر وضعیت.
// متن تیکت فقط از Desk کانال‌ها (TenantScope) خوانده می‌شود و هیچ داده مراجعه‌کننده‌ای ضمیمه نیست.
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type SupportTicketQueueItem, type TicketStatus } from '@phoenix/api-client';
import { usePlatformSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  ErrorState,
  faDigits,
  JalaliDate,
  KeyValueList,
  ListPage,
  LoadingState,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { consoleKeys } from '../../api';

export const CONSOLE_SUPPORT_TICKETS = 'CONSOLE-SUPPORT-TICKETS';
// وضعیت‌های قابل انتخاب در پاسخ پشتیبانی پلتفرم (Assigned و Cancelled فقط برای تیکت‌های مرکز — C-AGENT-1500).
type ReplyStatus = Extract<TicketStatus, 'InProgress' | 'WaitingOnRequester' | 'Resolved'>;
const REPLY_STATUSES: readonly ReplyStatus[] = ['InProgress', 'WaitingOnRequester', 'Resolved'];

function TicketDialog({ ticketId, onClose }: { ticketId: string; onClose: () => void }) {
  const { t } = useTranslation('console');
  const { api } = usePlatformSession();
  const queryClient = useQueryClient();
  const titleId = useId();
  const [body, setBody] = useState('');
  const [status, setStatus] = useState<ReplyStatus>('WaitingOnRequester');

  const ticket = useQuery({
    queryKey: consoleKeys.ticket(ticketId),
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/platform/console/support-tickets/{ticketId}', { params: { path: { ticketId } } }),
      ),
  });
  const reply = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/api/v1/platform/console/support-tickets/{ticketId}/messages', {
          params: { path: { ticketId } },
          body: { body: body.trim(), status },
        }),
      ),
    onSuccess: async (updated) => {
      setBody('');
      queryClient.setQueryData(consoleKeys.ticket(ticketId), updated);
      await queryClient.invalidateQueries({ queryKey: consoleKeys.tickets, exact: false });
    },
  });
  const screenshot = useMutation({
    mutationFn: async () => {
      const result = await api.GET('/api/v1/platform/console/support-tickets/{ticketId}/screenshot', {
        params: { path: { ticketId } },
        parseAs: 'blob',
      });
      if (!result.response.ok || !result.data) {
        throw result.error ?? new Error('screenshot');
      }
      const url = URL.createObjectURL(result.data as Blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
  });

  const data = ticket.data;
  return (
    <Dialog open onClose={onClose} aria-labelledby={titleId} maxWidth="md" fullWidth>
      <DialogTitle id={titleId}>
        {data ? `#${faDigits(String(data.number))} · ${data.subject}` : t('console:tickets.detail')}
      </DialogTitle>
      <DialogContent>
        {ticket.isPending ? (
          <LoadingState rows={3} />
        ) : ticket.error || !data ? (
          <ErrorState error={ticket.error} compact />
        ) : (
          <Stack spacing={2} data-testid="ticket-detail">
            <KeyValueList
              columns={3}
              items={[
                {
                  labelKey: 'console:tickets.category',
                  value: t(`console:tickets.categories.${data.category}`, { defaultValue: data.category }),
                },
                {
                  labelKey: 'console:tickets.status_label',
                  value: t(`console:tickets.status.${data.status}`),
                },
                { labelKey: 'console:tickets.created_by', value: data.createdByName },
                {
                  labelKey: 'console:tickets.app_version',
                  value: <bdi dir="ltr">{data.appVersion ?? '—'}</bdi>,
                },
                {
                  labelKey: 'console:tickets.route',
                  value: <bdi dir="ltr">{data.routePath ?? '—'}</bdi>,
                  testId: 'ticket-route',
                },
                { labelKey: 'console:tickets.trace_id', value: <bdi dir="ltr">{data.traceId ?? '—'}</bdi> },
              ]}
            />
            <Typography sx={{ whiteSpace: 'pre-wrap' }}>{data.description}</Typography>
            {data.hasScreenshot && (
              <Stack direction="row">
                <Button
                  variant="outlined"
                  onClick={() => screenshot.mutate()}
                  disabled={screenshot.isPending}
                  data-testid="ticket-screenshot"
                >
                  {t('console:tickets.screenshot')}
                </Button>
              </Stack>
            )}
            {data.messages.map((message) => (
              <Paper
                key={message.id}
                variant="outlined"
                sx={{ p: 1, bgcolor: message.author === 'Support' ? 'action.hover' : undefined }}
              >
                <Typography variant="caption" color="text.secondary">
                  {message.authorName} · <JalaliDate value={message.createdAt} withTime />
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {message.body}
                </Typography>
              </Paper>
            ))}
            {data.status !== 'Closed' && (
              <Stack spacing={1}>
                <TextField
                  label={t('console:tickets.reply')}
                  value={body}
                  onChange={(event) => setBody(event.target.value)}
                  multiline
                  minRows={3}
                  fullWidth
                  slotProps={{ htmlInput: { maxLength: 4000, 'data-testid': 'console-ticket-reply' } }}
                />
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    select
                    size="small"
                    label={t('console:tickets.new_status')}
                    value={status}
                    onChange={(event) => setStatus(event.target.value as ReplyStatus)}
                    sx={{ minWidth: 220 }}
                  >
                    {REPLY_STATUSES.map((value) => (
                      <MenuItem key={value} value={value}>
                        {t(`console:tickets.status.${value}`)}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button
                    variant="contained"
                    onClick={() => reply.mutate()}
                    disabled={!body.trim() || reply.isPending}
                    data-testid="console-ticket-send"
                  >
                    {t('console:tickets.send')}
                  </Button>
                </Stack>
              </Stack>
            )}
            {reply.error || screenshot.error ? (
              <ErrorState error={reply.error ?? screenshot.error} compact />
            ) : null}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common:actions.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

export function SupportTicketsPage() {
  const { t } = useTranslation('console');
  const { api } = usePlatformSession();
  const [awaitingOnly, setAwaitingOnly] = useState(true);
  const [open, setOpen] = useState<string | null>(null);

  const queue = useQuery({
    queryKey: [...consoleKeys.tickets, awaitingOnly],
    queryFn: () =>
      unwrap(api.GET('/api/v1/platform/console/support-tickets', { params: { query: { awaitingOnly } } })),
  });

  const columns: DataTableColumn<SupportTicketQueueItem>[] = [
    { id: 'tenant', headerKey: 'console:tickets.tenant', cell: (row) => row.tenantName },
    {
      id: 'category',
      headerKey: 'console:tickets.category',
      cell: (row) => t(`console:tickets.categories.${row.category}`, { defaultValue: row.category }),
    },
    {
      id: 'status',
      headerKey: 'console:tickets.status_label',
      cell: (row) => (
        <Stack direction="row" spacing={0.5}>
          <span>{t(`console:tickets.status.${row.status}`, { defaultValue: row.status })}</span>
          {row.awaitingSupport && (
            <Chip size="small" color="warning" label={t('console:tickets.awaiting_support')} />
          )}
        </Stack>
      ),
    },
    {
      id: 'opened',
      headerKey: 'console:tickets.opened_at',
      cell: (row) => <JalaliDate value={row.openedAt} withTime />,
    },
    {
      id: 'activity',
      headerKey: 'console:tickets.last_activity',
      cell: (row) => <JalaliDate value={row.lastActivityAt} withTime />,
    },
  ];

  return (
    <ListPage
      screenId={CONSOLE_SUPPORT_TICKETS}
      titleKey="console:tickets.title"
      subtitleKey="console:tickets.subtitle"
      filters={
        <FormControlLabel
          control={
            <Switch
              checked={awaitingOnly}
              onChange={(event) => setAwaitingOnly(event.target.checked)}
              data-testid="tickets-awaiting-only"
            />
          }
          label={t('console:tickets.awaiting_only')}
        />
      }
    >
      <DataTable
        captionKey="console:tickets.caption"
        columns={columns}
        rows={queue.data?.items}
        getRowId={(row) => row.ticketId}
        isLoading={queue.isPending}
        error={queue.error}
        onRetry={() => void queue.refetch()}
        emptyTitleKey="console:tickets.empty"
        onRowClick={(row) => setOpen(row.ticketId)}
      />
      {open && <TicketDialog ticketId={open} onClose={() => setOpen(null)} />}
    </ListPage>
  );
}
